import React from 'react';
import PropTypes from 'prop-types';
import { cx, css } from 'emotion';
import { ThemeContext } from '@hig/theme-context';
import { ControlBehavior } from '@hig/behaviors';

var variants = Object.freeze({
  LINE: "line",
  BOX: "box",
  PLAIN: "plain"
});

var availableVariants = Object.freeze(Object.values(variants));

var tagNames = Object.freeze({
  INPUT: "input",
  TEXTAREA: "textarea"
});

var availableTagNames = Object.freeze(Object.values(tagNames));

var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function inputStyles(_ref, themeData) {
  var isDisabled = _ref.isDisabled,
      hasFocus = _ref.hasFocus,
      hasHover = _ref.hasHover;

  var defaults = {
    color: themeData["input.value.fontColor"],
    height: "calc(" + themeData["input.minHeight"] + " - 2px)",
    transitionProperty: "color",
    transitionDuration: "0.3s"
  };

  if (isDisabled) {
    return _extends({}, defaults);
  }
  if (hasFocus) {
    return _extends({}, defaults, {
      transitionDuration: "0.1s"
    });
  }
  if (hasHover) {
    return _extends({}, defaults, {
      transitionDuration: "0.1s"
    });
  }
  return defaults;
}

function stylesheet(props, themeData) {
  return {
    input: _extends({
      backgroundColor: props.variant === variants.BOX ? themeData["input.box.default.backgroundColor"] : "transparent",
      boxSizing: "border-box",
      border: "none",
      appearance: "none",
      paddingTop: themeData["input.paddingVertical"],
      paddingRight: props.variant === variants.BOX ? themeData["input.box.paddingHorizontal"] : themeData["input.line.paddingHorizontal"],
      paddingBottom: themeData["input.paddingVertical"],
      paddingLeft: props.variant === variants.BOX ? themeData["input.box.paddingHorizontal"] : themeData["input.line.paddingHorizontal"],
      outline: "none",
      fontSize: themeData["input.value.fontSize"],
      fontFamily: themeData["input.value.fontFamily"],
      fontWeight: themeData["input.value.fontWeight"],
      lineHeight: themeData["input.value.lineHeight"],
      width: "100%",
      "&::placeholder": {
        color: themeData["input.value.placeholderFontColor"]
      },
      "&::-ms-input-placeholder": {
        color: themeData["input.value.placeholderFontColor"]
      },
      "::selection": {
        backgroundColor: themeData["input.value.highlightColor"]
      }
    }, inputStyles(props, themeData))
  };
}

var _extends$1 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _objectWithoutProperties(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function InputPresenter(props) {
  var disabled = props.disabled,
      hasFocus = props.hasFocus,
      hasHover = props.hasHover,
      inputRef = props.inputRef,
      onBlur = props.onBlur,
      onFocus = props.onFocus,
      onMouseEnter = props.onMouseEnter,
      onMouseLeave = props.onMouseLeave,
      tagName = props.tagName,
      customStylesheet = props.stylesheet,
      variant = props.variant,
      otherProps = _objectWithoutProperties(props, ["disabled", "hasFocus", "hasHover", "inputRef", "onBlur", "onFocus", "onMouseEnter", "onMouseLeave", "tagName", "stylesheet", "variant"]);

  return React.createElement(
    ThemeContext.Consumer,
    null,
    function (_ref) {
      var resolvedRoles = _ref.resolvedRoles,
          metadata = _ref.metadata;

      var styles = stylesheet({ isDisabled: disabled, hasFocus: hasFocus, hasHover: hasHover, variant: variant }, resolvedRoles);
      var cssStyles = customStylesheet ? customStylesheet(styles, props, resolvedRoles, metadata.colorSchemeId) : styles;

      var className = css(cssStyles.input);
      var customClassName = otherProps.className;

      if (customClassName) {
        className = cx(className, variant === variants.PLAIN ? customClassName : customClassName.split(" ").reduce(function (acc, cur) {
          return cx(acc, cur.trim() + "__input");
        }, ""));
      }

      var tagNameKey = tagName.toUpperCase();
      var Element = tagNames[tagNameKey];

      return React.createElement(Element, _extends$1({}, otherProps, {
        className: className,
        disabled: disabled,
        onBlur: onBlur,
        onFocus: onFocus,
        onMouseEnter: onMouseEnter,
        onMouseLeave: onMouseLeave,
        ref: inputRef
      }));
    }
  );
}

InputPresenter.propTypes = {
  disabled: PropTypes.bool,
  hasFocus: PropTypes.bool,
  hasHover: PropTypes.bool,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  stylesheet: PropTypes.func,
  tagName: PropTypes.oneOf(availableTagNames),
  variant: PropTypes.oneOf(availableVariants)
};

InputPresenter.defaultProps = {
  tagName: tagNames.INPUT
};
InputPresenter.__docgenInfo = {
  "description": "",
  "displayName": "InputPresenter",
  "props": {
    "disabled": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "hasFocus": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "hasHover": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "onBlur": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onFocus": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onMouseEnter": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "onMouseLeave": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "tagName": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "availableTagNames"
      },
      "required": false,
      "description": "",
      "defaultValue": {
        "value": "tagNames.INPUT",
        "computed": true
      }
    },
    "variant": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "availableVariants"
      },
      "required": false,
      "description": ""
    }
  }
};

var _extends$2 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function borderStyles(_ref, themeData) {
  var variant = _ref.variant;

  var defaults = {
    borderStyle: "solid",
    borderColor: "transparent",
    borderWidth: themeData["input.borderWidth"],
    borderBottomColor: themeData["input.line.default.borderBottomColor"]
  };

  return variant === variants.BOX ? _extends$2({}, defaults, {
    borderColor: themeData["input.box.default.borderColor"],
    borderBottomColor: themeData["input.box.default.borderColor"]
  }) : defaults;
}

function borderBottomStyles(_ref2, themeData) {
  var isDisabled = _ref2.isDisabled,
      hasFocus = _ref2.hasFocus,
      hasHover = _ref2.hasHover,
      error = _ref2.error;

  var defaults = {
    borderBottomStyle: "solid",
    borderBottomWidth: themeData["input.borderBottomWidth"]
  };

  if (isDisabled) {
    return _extends$2({}, defaults, {
      opacity: themeData["input.disabled.opacity"]
    });
  }

  return _extends$2({}, defaults, error ? { borderBottomColor: themeData["input.error.borderBottomColor"] } : {}, hasFocus ? { borderBottomColor: themeData["input.line.focus.borderBottomColor"] } : {}, hasHover ? { borderBottomColor: themeData["input.box.hover.borderBottomColor"] } : {});
}

function haloStyles(_ref3, themeData) {
  var isDisabled = _ref3.isDisabled,
      hasFocus = _ref3.hasFocus,
      hasHover = _ref3.hasHover;

  var defaults = {
    height: 0,
    transitionProperty: "height, color",
    transitionDuration: "0.3s, 0.3s"
  };
  var focusStyles = {
    height: themeData["input.haloWidth"],
    backgroundColor: themeData["input.focus.haloColor"],
    transitionDuration: "0.1s, 0.1s"
  };
  var hoverStyles = {
    backgroundColor: themeData["input.hover.haloColor"],
    height: themeData["input.haloWidth"],
    transitionDuration: "0.1s, 0.1s"
  };

  if (isDisabled) {
    return {};
  }

  return _extends$2({}, defaults, hasFocus ? focusStyles : {}, hasHover ? hoverStyles : {});
}

function stylesheet$1 (props, themeData) {
  return {
    wrapper: _extends$2({
      flexGrow: 2,
      position: "relative"
    }, borderStyles(props, themeData), borderBottomStyles(props, themeData)),
    halo: _extends$2({
      position: "absolute",
      top: "calc(100% + 1px)",
      left: "-1px",
      right: 0,
      width: "calc(100% + 2px)"
    }, haloStyles(props, themeData))
  };
}

function _objectWithoutProperties$1(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function InputHaloPresenter(props) {
  var children = props.children,
      hasFocus = props.hasFocus,
      hasHover = props.hasHover,
      isDisabled = props.isDisabled,
      customStylesheet = props.stylesheet,
      variant = props.variant,
      error = props.error,
      otherProps = _objectWithoutProperties$1(props, ["children", "hasFocus", "hasHover", "isDisabled", "stylesheet", "variant", "error"]);

  return React.createElement(
    ThemeContext.Consumer,
    null,
    function (_ref) {
      var resolvedRoles = _ref.resolvedRoles,
          metadata = _ref.metadata;

      var styles = stylesheet$1({ isDisabled: isDisabled, hasFocus: hasFocus, hasHover: hasHover, variant: variant, error: error }, resolvedRoles);
      var cssStyles = customStylesheet ? customStylesheet(styles, props, resolvedRoles, metadata.colorSchemeId) : styles;

      var className = otherProps.className;


      return React.createElement(
        "div",
        { className: cx(css(cssStyles.wrapper), className) },
        children,
        React.createElement("div", {
          className: cx(css(cssStyles.halo), className ? className.split(" ").reduce(function (acc, cur) {
            return cx(acc, cur.trim() + "__halo");
          }, "") : "")
        })
      );
    }
  );
}

InputHaloPresenter.propTypes = {
  children: PropTypes.node,
  hasFocus: PropTypes.bool,
  hasHover: PropTypes.bool,
  isDisabled: PropTypes.bool,
  stylesheet: PropTypes.func,
  variant: PropTypes.oneOf(availableVariants),
  error: PropTypes.bool
};
InputHaloPresenter.__docgenInfo = {
  "description": "",
  "displayName": "InputHaloPresenter",
  "props": {
    "children": {
      "type": {
        "name": "node"
      },
      "required": false,
      "description": ""
    },
    "hasFocus": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "hasHover": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "isDisabled": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": ""
    },
    "variant": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "availableVariants"
      },
      "required": false,
      "description": ""
    },
    "error": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": ""
    }
  }
};

var _extends$3 = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

function _objectWithoutProperties$2(obj, keys) { var target = {}; for (var i in obj) { if (keys.indexOf(i) >= 0) continue; if (!Object.prototype.hasOwnProperty.call(obj, i)) continue; target[i] = obj[i]; } return target; }

function Wrapper(props) {
  if (props.variant === variants.PLAIN) {
    return props.children;
  }

  return React.createElement(InputHaloPresenter, props);
}

Wrapper.propTypes = {
  children: PropTypes.node,
  tagName: PropTypes.oneOf(availableTagNames),
  variant: PropTypes.oneOf(availableVariants)
};

function Input(props) {
  var onFocusProp = props.onFocus,
      onBlurProp = props.onBlur,
      onMouseEnterProp = props.onMouseEnter,
      onMouseLeaveProp = props.onMouseLeave,
      disabledProp = props.disabled,
      inputRef = props.inputRef,
      stylesheet = props.stylesheet,
      tagName = props.tagName,
      variant = props.variant,
      error = props.error,
      otherProps = _objectWithoutProperties$2(props, ["onFocus", "onBlur", "onMouseEnter", "onMouseLeave", "disabled", "inputRef", "stylesheet", "tagName", "variant", "error"]);

  return React.createElement(
    ControlBehavior,
    {
      onFocus: onFocusProp,
      onBlur: onBlurProp,
      onMouseEnter: onMouseEnterProp,
      onMouseLeave: onMouseLeaveProp
    },
    function (_ref) {
      var hasHover = _ref.hasHover,
          onMouseEnter = _ref.onMouseEnter,
          onMouseLeave = _ref.onMouseLeave,
          hasFocus = _ref.hasFocus,
          onFocus = _ref.onFocus,
          onBlur = _ref.onBlur;
      return React.createElement(
        Wrapper,
        _extends$3({
          isDisabled: disabledProp,
          hasFocus: hasFocus,
          hasHover: hasHover,
          stylesheet: stylesheet,
          tagName: tagName,
          variant: variant,
          error: error
        }, otherProps),
        React.createElement(InputPresenter, _extends$3({
          disabled: disabledProp,
          hasFocus: hasFocus,
          hasHover: hasHover,
          inputRef: inputRef,
          onBlur: onBlur,
          onFocus: onFocus,
          onMouseEnter: onMouseEnter,
          onMouseLeave: onMouseLeave,
          stylesheet: stylesheet,
          tagName: tagName,
          variant: variant
        }, otherProps))
      );
    }
  );
}

Input.propTypes = {
  /**
   * The initial value of the control
   */
  defaultValue: PropTypes.string,
  /**
   * Prevents the user from interacting with the input
   */
  disabled: PropTypes.bool,
  /**
   * A callback ref that gets passed to the HTML input
   */
  inputRef: PropTypes.func,
  /**
   * Fired when an element has lost focus
   */
  onBlur: PropTypes.func,
  /**
   * Fired when an element has received focus
   */
  onFocus: PropTypes.func,
  /**
   * Fired when a pointing device is moved over the element
   */
  onMouseEnter: PropTypes.func,
  /**
   * Fired when the pointer of a pointing device is moved out of an element
   */
  onMouseLeave: PropTypes.func,
  /**
   * Adds custom/overriding styles
   */
  stylesheet: PropTypes.func,
  /**
   * Renders the HTML element that is passed in
   */
  tagName: PropTypes.oneOf(availableTagNames),
  /**
   * The value of the control
   */
  value: PropTypes.string,
  /**
   * The visual variant of the input
   */
  variant: PropTypes.oneOf(availableVariants),
  /**
   * Specify if the value of the input is wrong
   */
  error: PropTypes.bool
};

Input.defaultProps = {
  tagName: tagNames.INPUT,
  variant: variants.LINE,
  error: false
};
Input.__docgenInfo = {
  "description": "",
  "displayName": "Input",
  "props": {
    "defaultValue": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "The initial value of the control"
    },
    "disabled": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Prevents the user from interacting with the input"
    },
    "inputRef": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "A callback ref that gets passed to the HTML input"
    },
    "onBlur": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Fired when an element has lost focus"
    },
    "onFocus": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Fired when an element has received focus"
    },
    "onMouseEnter": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Fired when a pointing device is moved over the element"
    },
    "onMouseLeave": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Fired when the pointer of a pointing device is moved out of an element"
    },
    "stylesheet": {
      "type": {
        "name": "func"
      },
      "required": false,
      "description": "Adds custom/overriding styles"
    },
    "tagName": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "availableTagNames"
      },
      "required": false,
      "description": "Renders the HTML element that is passed in",
      "defaultValue": {
        "value": "tagNames.INPUT",
        "computed": true
      }
    },
    "value": {
      "type": {
        "name": "string"
      },
      "required": false,
      "description": "The value of the control"
    },
    "variant": {
      "type": {
        "name": "enum",
        "computed": true,
        "value": "availableVariants"
      },
      "required": false,
      "description": "The visual variant of the input",
      "defaultValue": {
        "value": "variants.LINE",
        "computed": true
      }
    },
    "error": {
      "type": {
        "name": "bool"
      },
      "required": false,
      "description": "Specify if the value of the input is wrong",
      "defaultValue": {
        "value": "false",
        "computed": false
      }
    }
  }
};

export default Input;
export { variants, availableVariants, tagNames, availableTagNames };
